'use client';

import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { apiConfig, getSelfOriginForRedirects } from '@/config/apiConfig';
import { extractApiMessage } from './Axios';

/**
 * Design Service axios provider — `design-service.boostt.ai` (Studio documents:
 * `GET/POST /api/designs`, `/api/templates`, `/api/docs`). Deliberately simple,
 * same spirit as `MailhouseAxios.tsx`: no sub-tenant/RBAC member-scoping concept
 * exists in this service, so none of `DirectMailAxios`/`QRCodeAxios`'s aggregate
 * account machinery is replicated here.
 *
 * AUTH: the service's own docs describe list endpoints as "browser sessions are
 * scoped to their own user_id" — session/cookie language, not token language.
 * This app's login is frequently cookie-only: `getAuthData().token` silently
 * falls back to a copy of `userId` when no real JWT exists (see
 * `Authentication.js`'s `getAuthData`), so attaching that as `Authorization:
 * Bearer <token>` would often mean sending a meaningless value. Worse, adding
 * a custom `Authorization` header makes the request non-simple and forces a
 * CORS preflight — and a manual OPTIONS preflight against `/api/designs`
 * (Origin set, no auth) returned HTTP 400 in testing, despite carrying correct
 * `Access-Control-Allow-*` headers. A plain GET with no custom headers (so no
 * preflight) came back as a clean 401 with correct CORS headers instead.
 *
 * So this client deliberately sends NO Authorization header — only
 * `withCredentials: true`, relying on the real cross-domain `.boostt.ai`
 * session cookie the browser already holds (this app uses `js-cookie` for
 * auth). That keeps every request "simple" (no preflight) and matches the
 * service's own "session" language. If this still 401s for a logged-in user,
 * the next thing to check is whether that session cookie is actually scoped
 * to `Domain=.boostt.ai` (vs. e.g. the app's own narrower host) — that's a
 * backend/login-flow question, not something this file can fix.
 */

export const DesignServiceAxiosContext = createContext<AxiosInstance | null>(null);

export const isCancel = base.isCancel;

function getDesignServiceInstance(
  auth: { token?: string | null; isAuthenticated?: boolean },
  setSessionExpiredOpen: (open: boolean) => void
): AxiosInstance {
  const axios = base.create({
    baseURL: apiConfig.baseUrls.designService,
    withCredentials: true,
  });

  // No custom request headers here on purpose — `X-Frontend-Page` (used by every other
  // client) and `Authorization` are both outside design-service's CORS
  // `Access-Control-Allow-Headers` allowlist (`authorization,content-type,cookie,x-master-token`
  // — note `authorization` IS allowed, but see the class doc above for why we don't send it)
  // or force a non-simple request. Adding either here would trigger a CORS preflight, and this
  // service's OPTIONS preflight returned HTTP 400 in testing — so anything that forces a
  // preflight risks the request never going out at all. Keep this a "simple" request
  // (GET/POST with only safelisted headers) so the browser skips preflight entirely.
  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth?.isAuthenticated) {
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError =
            (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError = 'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );

  return axios;
}

export default function DesignServiceAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const auth = authData || {};
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  const client = useMemo(
    () => getDesignServiceInstance(auth, setSessionExpiredOpen),
    [authData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    if (typeof window !== 'undefined') {
      location.href = `${getSelfOriginForRedirects()}/v2/login`;
    }
  };

  return (
    <>
      <DesignServiceAxiosContext.Provider value={client}>{children}</DesignServiceAxiosContext.Provider>

      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
